<template>
  <v-container fluid>
    <v-row class="mb-2">
      <v-col cols="12">
        <v-btn text to="/platform/businesses">
          <v-icon left>mdi-arrow-left</v-icon>
          Volver
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="loading">
      <v-col cols="12">
        <v-skeleton-loader type="article" />
      </v-col>
    </v-row>

    <template v-else-if="business">
      <v-row>
        <v-col cols="12" md="8">
          <v-card class="mb-4">
            <v-card-title class="d-flex justify-space-between align-center">
              <span>{{ business.name }}</span>
              <BusinessStatusChip :status="business.status" />
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6"><strong>Base de datos:</strong> {{ business.dbName }}</v-col>
                <v-col cols="12" md="6"><strong>Usuario MySQL:</strong> {{ business.dbUser || '-' }}</v-col>
                <v-col cols="12" md="6"><strong>Correo referencia:</strong> {{ business.adminEmail || '-' }}</v-col>
                <v-col cols="12" md="6"><strong>Dirección:</strong> {{ business.address || '-' }}</v-col>
                <v-col cols="12"><strong>Notas:</strong> {{ business.notes || '-' }}</v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-btn
                v-if="business.status === 'active'"
                color="orange darken-2"
                dark
                :loading="actionLoading"
                @click="updateStatus('suspended')"
              >
                Suspender
              </v-btn>
              <v-btn
                v-if="business.status === 'suspended'"
                color="green darken-1"
                dark
                :loading="actionLoading"
                @click="updateStatus('active')"
              >
                Reactivar
              </v-btn>
              <v-btn
                v-if="accounts.length"
                outlined
                :loading="actionLoading"
                @click="resendInvite"
              >
                Reenviar invitación
              </v-btn>
            </v-card-actions>
          </v-card>

          <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
              <span>Suscripción</span>
              <SubscriptionStatusChip v-if="subscription" :status="subscription.status" />
            </v-card-title>
            <v-card-text v-if="subscription">
              <p class="mb-1"><strong>Plan:</strong> {{ subscription.plan }} ({{ subscription.planId }})</p>
              <p class="mb-1"><strong>Periodo:</strong> {{ formatDate(subscription.currentPeriodStart) }} → {{ formatDate(subscription.currentPeriodEnd) }}</p>
              <p class="mb-1"><strong>Facturación:</strong> {{ subscription.billingInterval === 'year' ? 'Anual' : 'Mensual' }}</p>
              <p class="mb-0"><strong>Proveedor:</strong> {{ subscription.externalProvider || 'manual' }}</p>
            </v-card-text>
            <v-card-text v-else class="grey--text">
              Sin suscripción registrada.
            </v-card-text>
            <v-card-actions>
              <v-btn color="primary" text @click="openSubscriptionDialog">
                {{ subscription ? 'Editar suscripción' : 'Registrar suscripción' }}
              </v-btn>
              <v-btn
                v-if="subscription && subscription.status !== 'canceled'"
                text
                color="red darken-2"
                :loading="actionLoading"
                @click="cancelSubscription"
              >
                Cancelar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
              <span>Cuentas web</span>
              <v-btn small color="primary" text @click="openAccountDialog">
                <v-icon left small>mdi-account-plus</v-icon>
                Agregar
              </v-btn>
            </v-card-title>
            <v-list dense>
              <v-list-item v-for="account in accounts" :key="account.id">
                <v-list-item-content>
                  <v-list-item-title>{{ account.identifier }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ account.role }} · {{ account.isActive ? 'Activa' : 'Inactiva' }}
                    <span v-if="!account.invitationSentAt" class="orange--text text--darken-2"> · Invitación pendiente</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="d-flex flex-row">
                  <v-btn
                    icon
                    small
                    title="Reenviar invitación"
                    :loading="actionLoading"
                    @click="resendAccountInvite(account.id)"
                  >
                    <v-icon small>mdi-email-sync</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    title="Restablecer contraseña"
                    :loading="actionLoading"
                    @click="resetPassword(account.id)"
                  >
                    <v-icon small>mdi-lock-reset</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-list-item v-if="!accounts.length">
                <v-list-item-content>
                  <v-list-item-title class="grey--text">Sin cuentas de plataforma</v-list-item-title>
                  <v-list-item-subtitle>El negocio puede operar solo con BD MySQL</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-4">
        <v-col cols="12">
          <v-card>
            <v-card-title>Historial de provisioning</v-card-title>
            <v-data-table
              :headers="logHeaders"
              :items="logs"
              :items-per-page="10"
              class="elevation-0"
            >
              <template v-slot:[`item.status`]="{ item }">
                <v-chip small :color="item.status === 'success' ? 'green lighten-4' : 'red lighten-4'" label>
                  {{ item.status }}
                </v-chip>
              </template>
              <template v-slot:[`item.createdAt`]="{ item }">
                {{ formatDate(item.createdAt) }}
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-dialog v-model="accountDialog" max-width="520" persistent>
      <v-card>
        <v-card-title>Nueva cuenta de plataforma</v-card-title>
        <v-card-text>
          <PlatformAccountForm ref="accountForm" @submit="saveAccount" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeAccountDialog">Cancelar</v-btn>
          <v-btn color="primary" :loading="actionLoading" @click="submitAccountForm">Crear cuenta</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="subscriptionDialog" max-width="720">
      <v-card>
        <v-card-title>{{ subscription ? 'Editar suscripción' : 'Registrar suscripción' }}</v-card-title>
        <v-card-text>
          <SubscriptionFields ref="subscriptionEditor" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="subscriptionDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="actionLoading" @click="saveSubscription">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="credentialsDialog" max-width="520">
      <v-card>
        <v-card-title>Credenciales de acceso</v-card-title>
        <v-card-text v-if="credentials">
          <v-alert type="warning" dense outlined class="mb-3">Guárdalas ahora; no se volverán a mostrar.</v-alert>
          <p class="text-caption grey--text mb-3">Usa estos datos si el correo no llegó o aún no tienes Resend configurado.</p>
          <p><strong>Usuario:</strong> {{ credentials.webLogin }}</p>
          <p><strong>Contraseña temporal:</strong> {{ credentials.webTemporaryPassword }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="credentialsDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import BusinessStatusChip from '@/components/platform/BusinessStatusChip.vue';
import SubscriptionStatusChip from '@/components/platform/SubscriptionStatusChip.vue';
import SubscriptionFields from '@/components/platform/SubscriptionFields.vue';
import PlatformAccountForm from '@/components/platform/PlatformAccountForm.vue';
import { fetchBusinessById, updateBusiness, resendBusinessInvite } from '@/services/platform/businessService';
import { resetAccountPassword, createBusinessAccount, resendAccountInvite } from '@/services/platform/platformAccountService';
import {
  upsertBusinessSubscription,
  cancelBusinessSubscription,
} from '@/services/platform/subscriptionService';

export default {
  name: 'BusinessDetailView',
  components: { BusinessStatusChip, SubscriptionStatusChip, SubscriptionFields, PlatformAccountForm },
  data() {
    return {
      loading: true,
      actionLoading: false,
      business: null,
      accounts: [],
      subscription: null,
      logs: [],
      subscriptionDialog: false,
      accountDialog: false,
      credentialsDialog: false,
      credentials: null,
      logHeaders: [
        { text: 'Acción', value: 'action' },
        { text: 'Estado', value: 'status' },
        { text: 'Fecha', value: 'createdAt' },
      ],
    };
  },
  created() {
    this.loadDetail();
  },
  methods: {
    async loadDetail() {
      this.loading = true;
      try {
        const data = await fetchBusinessById(this.$route.params.id);
        this.business = data.business;
        this.accounts = data.accounts || [];
        this.subscription = data.subscription || null;
        this.logs = data.logs || [];
      } catch (error) {
        console.error(error);
        this.$router.push('/platform/businesses');
      } finally {
        this.loading = false;
      }
    },
    openSubscriptionDialog() {
      this.subscriptionDialog = true;
      this.$nextTick(() => {
        if (!this.$refs.subscriptionEditor) return;
        this.$refs.subscriptionEditor.initFromValues(this.subscription || {});
      });
    },
    openAccountDialog() {
      this.accountDialog = true;
      this.$nextTick(() => {
        if (this.$refs.accountForm) {
          this.$refs.accountForm.reset();
          if (this.business?.adminEmail) {
            this.$refs.accountForm.form.adminEmail = this.business.adminEmail;
          }
        }
      });
    },
    closeAccountDialog() {
      this.accountDialog = false;
    },
    submitAccountForm() {
      if (this.$refs.accountForm) {
        this.$refs.accountForm.submit();
      }
    },
    async saveAccount(payload) {
      this.actionLoading = true;
      try {
        const result = await createBusinessAccount(this.business.id, payload);
        this.accountDialog = false;

        if (result.credentials) {
          this.credentials = result.credentials;
          this.credentialsDialog = true;
          if (result.warning) {
            setTimeout(() => alert(result.warning), 100);
          }
        } else {
          alert(result.mensaje || 'Cuenta vinculada correctamente');
        }

        await this.loadDetail();
      } catch (error) {
        alert(error.response?.data?.mensaje || 'No se pudo crear la cuenta');
      } finally {
        this.actionLoading = false;
      }
    },
    async saveSubscription() {
      this.actionLoading = true;
      try {
        const payload = this.$refs.subscriptionEditor.getPayload();
        await upsertBusinessSubscription(this.business.id, payload);
        this.subscriptionDialog = false;
        await this.loadDetail();
      } catch (error) {
        alert(error.response?.data?.mensaje || 'Error al guardar suscripción');
      } finally {
        this.actionLoading = false;
      }
    },
    async cancelSubscription() {
      if (!confirm('¿Cancelar la suscripción de este negocio?')) return;
      this.actionLoading = true;
      try {
        await cancelBusinessSubscription(this.business.id);
        await this.loadDetail();
      } catch (error) {
        alert(error.response?.data?.mensaje || 'Error al cancelar suscripción');
      } finally {
        this.actionLoading = false;
      }
    },
    async updateStatus(status) {
      this.actionLoading = true;
      try {
        await updateBusiness(this.business.id, { status });
        await this.loadDetail();
      } catch (error) {
        alert(error.response?.data?.mensaje || 'Error al actualizar estado');
      } finally {
        this.actionLoading = false;
      }
    },
    async resendAccountInvite(accountId) {
      this.actionLoading = true;
      try {
        const result = await resendAccountInvite(this.business.id, accountId);
        this.credentials = result.credentials;
        this.credentialsDialog = true;
        if (result.warning) {
          setTimeout(() => alert(result.warning), 100);
        }
        await this.loadDetail();
      } catch (error) {
        alert(error.response?.data?.mensaje || 'Error al reenviar invitación');
      } finally {
        this.actionLoading = false;
      }
    },
    async resendInvite() {
      this.actionLoading = true;
      try {
        const result = await resendBusinessInvite(this.business.id);
        this.credentials = result.credentials;
        this.credentialsDialog = true;
        if (result.warning) {
          setTimeout(() => alert(result.warning), 100);
        }
        await this.loadDetail();
      } catch (error) {
        alert(error.response?.data?.mensaje || 'Error al reenviar invitación');
      } finally {
        this.actionLoading = false;
      }
    },
    async resetPassword(accountId) {
      this.actionLoading = true;
      try {
        const result = await resetAccountPassword(accountId);
        this.credentials = result.credentials;
        this.credentialsDialog = true;
      } catch (error) {
        alert(error.response?.data?.mensaje || 'Error al restablecer contraseña');
      } finally {
        this.actionLoading = false;
      }
    },
    formatDate(value) {
      if (!value) return '-';
      return new Date(value).toLocaleString('es-MX');
    },
  },
};
</script>
